import React, { useState } from 'react';
import { MessageSquareIcon, PlusIcon, DownloadIcon, SearchIcon, FilterIcon, InfoIcon, AlertTriangleIcon, CheckCircleIcon, XCircleIcon, BellIcon, SettingsIcon, EyeIcon, EditIcon, TrashIcon, RefreshCwIcon, ClipboardIcon, MailIcon, SendIcon, ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { MessagesList } from './MessagesList';
import { MessageForm } from './MessageForm';
import { EmailTemplatesList } from './EmailTemplatesList';
import { EmailTemplateForm } from './EmailTemplateForm';
import { StatusBadge } from '../../StatusBadge';
// Message type definitions
export type MessageType = 'Error' | 'Warning' | 'Success' | 'Info' | 'Notification' | 'System';
export type MessageModule = 'AP' | 'PO' | 'CM' | 'EM' | 'OCR' | 'Settings' | 'Core' | 'VM' | 'DI' | 'Financial';
export interface Message {
  id: string;
  key: string;
  defaultMessage: string;
  overriddenMessage?: string;
  type: MessageType;
  module: MessageModule;
  context?: string;
  languageCode: string;
  isActive: boolean;
  isSystemLocked: boolean;
  editableByRoles: string[];
  createdAt: string;
  createdBy: string;
  modifiedAt?: string;
  modifiedBy?: string;
  lastUsedAt?: string;
  usageCount: number;
}
// Email template definition
export interface EmailTemplate {
  id: string;
  key: string;
  subject: string;
  bodyHtml: string;
  mergeFields: string[];
  module: MessageModule;
  languageCode: string;
  isActive: boolean;
  isSystemLocked: boolean;
  editableByRoles: string[];
  createdAt: string;
  createdBy: string;
  modifiedAt?: string;
  modifiedBy?: string;
  lastUsedAt?: string;
  usageCount: number;
}
// Role definitions for RBAC
export interface Role {
  id: string;
  name: string;
  description: string;
  canEditSystemMessages: boolean;
  canEditErrorMessages: boolean;
  canEditWarningMessages: boolean;
  canEditSuccessMessages: boolean;
  canEditInfoMessages: boolean;
  canEditNotificationMessages: boolean;
  canEditEmailTemplates: boolean;
}
export function MessagesManagement({
  locale
}) {
  // Tab state
  const [activeTab, setActiveTab] = useState<'messages' | 'email-templates'>('messages');
  // Messages state
  const [view, setView] = useState<'list' | 'add' | 'edit'>('list');
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    type: [] as MessageType[],
    module: [] as MessageModule[],
    hasOverride: false,
    isActive: true,
    hasRbacRestriction: false
  });
  const [showFilters, setShowFilters] = useState(false);
  // Email templates state
  const [emailView, setEmailView] = useState<'list' | 'add' | 'edit'>('list');
  const [selectedTemplate, setSelectedTemplate] = useState<EmailTemplate | null>(null);
  const [emailSearchTerm, setEmailSearchTerm] = useState('');
  const [emailFilters, setEmailFilters] = useState({
    module: [] as MessageModule[],
    isActive: true,
    hasRbacRestriction: false
  });
  const [showEmailFilters, setShowEmailFilters] = useState(false);
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Email templates pagination
  const [emailCurrentPage, setEmailCurrentPage] = useState(1);
  const [emailPageSize, setEmailPageSize] = useState(10);
  // Mock current user role - in a real app, this would come from auth context
  const currentUserRoles = ['Administrator'];
  // Sample roles for RBAC
  const availableRoles: Role[] = [{
    id: 'admin',
    name: 'Administrator',
    description: 'Full system access',
    canEditSystemMessages: true,
    canEditErrorMessages: true,
    canEditWarningMessages: true,
    canEditSuccessMessages: true,
    canEditInfoMessages: true,
    canEditNotificationMessages: true,
    canEditEmailTemplates: true
  }, {
    id: 'system-maintainer',
    name: 'System Maintainer',
    description: 'Technical system maintenance',
    canEditSystemMessages: true,
    canEditErrorMessages: true,
    canEditWarningMessages: true,
    canEditSuccessMessages: true,
    canEditInfoMessages: true,
    canEditNotificationMessages: true,
    canEditEmailTemplates: true
  }, {
    id: 'config-manager',
    name: 'Configuration Manager',
    description: 'System configuration',
    canEditSystemMessages: false,
    canEditErrorMessages: false,
    canEditWarningMessages: true,
    canEditSuccessMessages: true,
    canEditInfoMessages: true,
    canEditNotificationMessages: true,
    canEditEmailTemplates: true
  }, {
    id: 'notification-manager',
    name: 'Notification Manager',
    description: 'Manages notifications',
    canEditSystemMessages: false,
    canEditErrorMessages: false,
    canEditWarningMessages: false,
    canEditSuccessMessages: true,
    canEditInfoMessages: true,
    canEditNotificationMessages: true,
    canEditEmailTemplates: true
  }, {
    id: 'ocr-team',
    name: 'OCR Team',
    description: 'OCR processing team',
    canEditSystemMessages: false,
    canEditErrorMessages: false,
    canEditWarningMessages: false,
    canEditSuccessMessages: false,
    canEditInfoMessages: true,
    canEditNotificationMessages: false,
    canEditEmailTemplates: false
  }];
  // Sample messages data
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    key: 'AP_ERR_DUPLICATEINVOICE',
    defaultMessage: 'Invoice already exists.',
    type: 'Error',
    module: 'AP',
    context: 'InvoiceEntry',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'System Maintainer'],
    createdAt: '2023-01-15T09:30:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-20T14:22:00Z',
    usageCount: 47
  }, {
    id: '2',
    key: 'PO_SUCCESS_CREATED',
    defaultMessage: 'Purchase order created.',
    type: 'Success',
    module: 'PO',
    context: 'PurchaseOrderForm',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'Configuration Manager', 'Notification Manager'],
    createdAt: '2023-01-15T09:35:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-25T11:42:00Z',
    usageCount: 128
  }, {
    id: '3',
    key: 'OCR_INFO_REVIEW',
    defaultMessage: 'OCR extraction requires review.',
    type: 'Info',
    module: 'OCR',
    context: 'DocumentReview',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'OCR Team', 'Configuration Manager'],
    createdAt: '2023-01-15T09:40:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-26T15:18:00Z',
    usageCount: 312
  }, {
    id: '4',
    key: 'SYS_AUTH_FAIL',
    defaultMessage: 'Authentication failed. Please try again.',
    type: 'System',
    module: 'Core',
    context: 'Login',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: true,
    editableByRoles: [],
    createdAt: '2023-01-15T09:45:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-26T16:05:00Z',
    usageCount: 89
  }, {
    id: '5',
    key: 'VM_WARN_COMPLIANCE',
    defaultMessage: 'Vendor compliance documents will expire soon.',
    overriddenMessage: 'ATTENTION: Vendor compliance documents will expire within 30 days. Please update.',
    type: 'Warning',
    module: 'VM',
    context: 'VendorDetails',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'System Maintainer', 'Configuration Manager'],
    createdAt: '2023-01-15T09:50:00Z',
    createdBy: 'System',
    modifiedAt: '2023-03-10T11:15:00Z',
    modifiedBy: 'admin@example.com',
    lastUsedAt: '2023-05-24T10:22:00Z',
    usageCount: 56
  }, {
    id: '6',
    key: 'AP_NOTIFICATION_APPROVAL',
    defaultMessage: 'An invoice requires your approval.',
    type: 'Notification',
    module: 'AP',
    context: 'InvoiceApproval',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'Notification Manager'],
    createdAt: '2023-01-15T09:55:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-26T09:15:00Z',
    usageCount: 437
  }]);
  // Sample email templates data
  const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([{
    id: '1',
    key: 'PO_EMAIL_VENDOR_NEW_PO',
    subject: 'New Purchase Order {{PONumber}} from {{CompanyName}}',
    bodyHtml: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #4f46e5;">New Purchase Order</h2>
          <p>Dear {{VendorName}},</p>
          <p>A new purchase order has been created for your company.</p>
          <div style="background-color: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p><strong>Purchase Order:</strong> {{PONumber}}</p>
            <p><strong>Date:</strong> {{PODate}}</p>
            <p><strong>Total Amount:</strong> {{POAmount}}</p>
          </div>
          <p>Please review the attached purchase order document and confirm receipt.</p>
          <p>If you have any questions, please contact us at {{ContactEmail}}.</p>
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px;">This is an automated message from {{CompanyName}}.</p>
          </div>
        </div>
      `,
    mergeFields: ['PONumber', 'VendorName', 'CompanyName', 'PODate', 'POAmount', 'ContactEmail'],
    module: 'PO',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'Configuration Manager'],
    createdAt: '2023-01-20T10:30:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-26T14:22:00Z',
    usageCount: 78
  }, {
    id: '2',
    key: 'VM_EMAIL_ONBOARDING_WELCOME',
    subject: 'Welcome to {{CompanyName}} Vendor Portal',
    bodyHtml: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #4f46e5;">Welcome to Our Vendor Portal</h2>
          <p>Dear {{VendorName}},</p>
          <p>Thank you for registering with {{CompanyName}}. Your vendor account has been created successfully.</p>
          <div style="background-color: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p><strong>Vendor ID:</strong> {{VendorID}}</p>
            <p><strong>Portal URL:</strong> <a href="{{PortalURL}}">{{PortalURL}}</a></p>
          </div>
          <p>To complete your profile, please log in to the portal using the temporary credentials below:</p>
          <p><strong>Username:</strong> {{Username}}</p>
          <p><strong>Temporary Password:</strong> {{TempPassword}}</p>
          <p>You will be prompted to change your password upon first login.</p>
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px;">This is an automated message from {{CompanyName}}.</p>
          </div>
        </div>
      `,
    mergeFields: ['VendorName', 'CompanyName', 'VendorID', 'PortalURL', 'Username', 'TempPassword'],
    module: 'VM',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'Configuration Manager'],
    createdAt: '2023-01-22T11:15:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-25T10:18:00Z',
    usageCount: 45
  }, {
    id: '3',
    key: 'AP_EMAIL_INVOICE_APPROVAL_REQUEST',
    subject: 'Invoice Approval Required: {{InvoiceNumber}}',
    bodyHtml: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #4f46e5;">Invoice Approval Request</h2>
          <p>Hello {{ApproverName}},</p>
          <p>An invoice requires your approval.</p>
          <div style="background-color: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p><strong>Invoice Number:</strong> {{InvoiceNumber}}</p>
            <p><strong>Vendor:</strong> {{VendorName}}</p>
            <p><strong>Amount:</strong> {{InvoiceAmount}}</p>
            <p><strong>Due Date:</strong> {{DueDate}}</p>
          </div>
          <p>Please review and approve this invoice by clicking the button below:</p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{ApprovalLink}}" style="background-color: #4f46e5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">Review Invoice</a>
          </div>
          <p>If you have any questions, please contact the AP team.</p>
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px;">This is an automated message from {{CompanyName}}.</p>
          </div>
        </div>
      `,
    mergeFields: ['ApproverName', 'InvoiceNumber', 'VendorName', 'InvoiceAmount', 'DueDate', 'ApprovalLink', 'CompanyName'],
    module: 'AP',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'Notification Manager'],
    createdAt: '2023-01-25T14:20:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-26T16:42:00Z',
    usageCount: 215
  }, {
    id: '4',
    key: 'OCR_EMAIL_EXCEPTION_NOTIFICATION',
    subject: 'OCR Exception: Document Review Required',
    bodyHtml: `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <h2 style="color: #4f46e5;">OCR Exception Alert</h2>
          <p>Hello {{RecipientName}},</p>
          <p>An exception has been detected during OCR processing that requires your review.</p>
          <div style="background-color: #f3f4f6; padding: 15px; border-radius: 5px; margin: 20px 0;">
            <p><strong>Document ID:</strong> {{DocumentID}}</p>
            <p><strong>Exception Type:</strong> {{ExceptionType}}</p>
            <p><strong>Date Received:</strong> {{DateReceived}}</p>
          </div>
          <p>Please review this document by clicking the link below:</p>
          <div style="text-align: center; margin: 30px 0;">
            <a href="{{ReviewLink}}" style="background-color: #4f46e5; color: white; padding: 12px 24px; text-decoration: none; border-radius: 4px; font-weight: bold;">Review Document</a>
          </div>
          <div style="margin-top: 30px; padding-top: 20px; border-top: 1px solid #e5e7eb;">
            <p style="color: #6b7280; font-size: 14px;">This is an automated message from {{CompanyName}}.</p>
          </div>
        </div>
      `,
    mergeFields: ['RecipientName', 'DocumentID', 'ExceptionType', 'DateReceived', 'ReviewLink', 'CompanyName'],
    module: 'OCR',
    languageCode: 'en-AU',
    isActive: true,
    isSystemLocked: false,
    editableByRoles: ['Administrator', 'OCR Team'],
    createdAt: '2023-02-05T09:10:00Z',
    createdBy: 'System',
    lastUsedAt: '2023-05-25T11:32:00Z',
    usageCount: 67
  }]);
  // Check if user can edit a specific message based on type and roles
  const canEditMessage = (message: Message): boolean => {
    if (message.isSystemLocked) return false;
    // Check if any of the user's roles are in the message's editableByRoles
    const hasEditRole = currentUserRoles.some(role => message.editableByRoles.includes(role));
    return hasEditRole;
  };
  // Check if user can edit a specific email template based on roles
  const canEditEmailTemplate = (template: EmailTemplate): boolean => {
    if (template.isSystemLocked) return false;
    // Check if any of the user's roles are in the template's editableByRoles
    const hasEditRole = currentUserRoles.some(role => template.editableByRoles.includes(role));
    return hasEditRole;
  };
  // Message management handlers
  const handleAddMessage = () => {
    setSelectedMessage(null);
    setView('add');
  };
  const handleEditMessage = (message: Message) => {
    setSelectedMessage(message);
    setView('edit');
  };
  const handleSaveMessage = (message: Message) => {
    if (view === 'add') {
      setMessages([...messages, {
        ...message,
        id: `${messages.length + 1}`,
        createdAt: new Date().toISOString(),
        createdBy: 'admin@example.com',
        usageCount: 0
      }]);
    } else {
      setMessages(messages.map(m => m.id === message.id ? {
        ...message,
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'admin@example.com'
      } : m));
    }
    setView('list');
  };
  const handleDeleteMessage = (id: string) => {
    setMessages(messages.filter(m => m.id !== id));
  };
  const handleResetOverride = (id: string) => {
    setMessages(messages.map(m => m.id === id ? {
      ...m,
      overriddenMessage: undefined,
      modifiedAt: new Date().toISOString(),
      modifiedBy: 'admin@example.com'
    } : m));
  };
  // Email template management handlers
  const handleAddEmailTemplate = () => {
    setSelectedTemplate(null);
    setEmailView('add');
  };
  const handleEditEmailTemplate = (template: EmailTemplate) => {
    setSelectedTemplate(template);
    setEmailView('edit');
  };
  const handleSaveEmailTemplate = (template: EmailTemplate) => {
    if (emailView === 'add') {
      setEmailTemplates([...emailTemplates, {
        ...template,
        id: `${emailTemplates.length + 1}`,
        createdAt: new Date().toISOString(),
        createdBy: 'admin@example.com',
        usageCount: 0
      }]);
    } else {
      setEmailTemplates(emailTemplates.map(t => t.id === template.id ? {
        ...template,
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'admin@example.com'
      } : t));
    }
    setEmailView('list');
  };
  const handleDeleteEmailTemplate = (id: string) => {
    setEmailTemplates(emailTemplates.filter(t => t.id !== id));
  };
  const handleSendTestEmail = (templateId: string, testEmail: string) => {
    // In a real app, this would send a test email
    console.log(`Sending test email using template ${templateId} to ${testEmail}`);
    alert(`Test email sent to ${testEmail}`);
  };
  const handleExport = () => {
    // In a real app, this would export to CSV
    console.log('Exporting messages to CSV');
  };
  const handleExportTemplates = () => {
    // In a real app, this would export templates to a file
    console.log('Exporting email templates');
  };
  // Filter handling for messages
  const toggleFilter = (filterType: 'type' | 'module', value: MessageType | MessageModule) => {
    setFilters(prev => {
      const currentFilters = prev[filterType];
      return {
        ...prev,
        [filterType]: currentFilters.includes(value as any) ? currentFilters.filter(f => f !== value) : [...currentFilters, value]
      };
    });
    setCurrentPage(1); // Reset to first page when filtering
  };
  const resetFilters = () => {
    setFilters({
      type: [],
      module: [],
      hasOverride: false,
      isActive: true,
      hasRbacRestriction: false
    });
    setSearchTerm('');
    setCurrentPage(1); // Reset to first page when clearing filters
  };
  // Filter handling for email templates
  const toggleEmailFilter = (filterType: 'module', value: MessageModule) => {
    setEmailFilters(prev => {
      const currentFilters = prev[filterType];
      return {
        ...prev,
        [filterType]: currentFilters.includes(value as any) ? currentFilters.filter(f => f !== value) : [...currentFilters, value]
      };
    });
    setEmailCurrentPage(1); // Reset to first page when filtering
  };
  const resetEmailFilters = () => {
    setEmailFilters({
      module: [],
      isActive: true,
      hasRbacRestriction: false
    });
    setEmailSearchTerm('');
    setEmailCurrentPage(1); // Reset to first page when clearing filters
  };
  // Handle page changes for messages
  const goToPage = (page: number) => {
    if (page < 1) page = 1;
    if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };
  // Handle page size changes for messages
  const handlePageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };
  // Handle page changes for email templates
  const goToEmailPage = (page: number) => {
    if (page < 1) page = 1;
    if (page > emailTotalPages) page = emailTotalPages;
    setEmailCurrentPage(page);
  };
  // Handle page size changes for email templates
  const handleEmailPageSizeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newSize = parseInt(e.target.value);
    setEmailPageSize(newSize);
    setEmailCurrentPage(1); // Reset to first page when changing page size
  };
  // Filter messages based on search and filters
  const filteredMessages = messages.filter(message => {
    // Search term filter
    if (searchTerm && !message.key.toLowerCase().includes(searchTerm.toLowerCase()) && !message.defaultMessage.toLowerCase().includes(searchTerm.toLowerCase()) && !(message.overriddenMessage && message.overriddenMessage.toLowerCase().includes(searchTerm.toLowerCase()))) {
      return false;
    }
    // Type filter
    if (filters.type.length > 0 && !filters.type.includes(message.type)) {
      return false;
    }
    // Module filter
    if (filters.module.length > 0 && !filters.module.includes(message.module)) {
      return false;
    }
    // Has override filter
    if (filters.hasOverride && !message.overriddenMessage) {
      return false;
    }
    // Is active filter
    if (filters.isActive && !message.isActive) {
      return false;
    }
    // Has RBAC restriction filter
    if (filters.hasRbacRestriction && message.editableByRoles.length === 0) {
      return false;
    }
    return true;
  });
  // Filter email templates based on search and filters
  const filteredEmailTemplates = emailTemplates.filter(template => {
    // Search term filter
    if (emailSearchTerm && !template.key.toLowerCase().includes(emailSearchTerm.toLowerCase()) && !template.subject.toLowerCase().includes(emailSearchTerm.toLowerCase()) && !template.bodyHtml.toLowerCase().includes(emailSearchTerm.toLowerCase())) {
      return false;
    }
    // Module filter
    if (emailFilters.module.length > 0 && !emailFilters.module.includes(template.module)) {
      return false;
    }
    // Is active filter
    if (emailFilters.isActive && !template.isActive) {
      return false;
    }
    // Has RBAC restriction filter
    if (emailFilters.hasRbacRestriction && template.editableByRoles.length === 0) {
      return false;
    }
    return true;
  });
  // Calculate pagination for messages
  const totalMessages = filteredMessages.length;
  const totalPages = Math.ceil(totalMessages / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalMessages);
  const currentMessages = filteredMessages.slice(startIndex, endIndex);
  // Calculate pagination for email templates
  const totalEmailTemplates = filteredEmailTemplates.length;
  const emailTotalPages = Math.ceil(totalEmailTemplates / emailPageSize);
  const emailStartIndex = (emailCurrentPage - 1) * emailPageSize;
  const emailEndIndex = Math.min(emailStartIndex + emailPageSize, totalEmailTemplates);
  const currentEmailTemplates = filteredEmailTemplates.slice(emailStartIndex, emailEndIndex);
  return <div className="space-y-6" data-id="element-18694">
      <div className="flex justify-between items-center" data-id="element-18695">
        <div data-id="element-18696">
          <h2 className="text-2xl font-bold text-gray-900" data-id="element-18697">
            Messages & Templates
          </h2>
          <p className="mt-1 text-sm text-gray-500" data-id="element-18698">
            Manage system messages, alerts, and email templates across the
            application.
          </p>
        </div>
        {/* Add buttons for list views */}
        {activeTab === 'messages' && view === 'list' && <div className="flex space-x-3" data-id="element-18699">
            <button onClick={handleExport} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18700">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-18701" />
              Export
            </button>
            <button onClick={handleAddMessage} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18702">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-18703" />
              Add Message
            </button>
          </div>}
        {activeTab === 'email-templates' && emailView === 'list' && <div className="flex space-x-3" data-id="element-18704">
            <button onClick={handleExportTemplates} className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18705">
              <DownloadIcon className="h-4 w-4 mr-2" data-id="element-18706" />
              Export
            </button>
            <button onClick={handleAddEmailTemplate} className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18707">
              <PlusIcon className="h-4 w-4 mr-2" data-id="element-18708" />
              Add Template
            </button>
          </div>}
      </div>
      {/* Tab navigation */}
      <div className="border-b border-gray-200" data-id="element-18709">
        <nav className="-mb-px flex space-x-8" data-id="element-18710">
          <button onClick={() => setActiveTab('messages')} className={`${activeTab === 'messages' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-18711">
            <MessageSquareIcon className="h-5 w-5 mr-2" data-id="element-18712" />
            System Messages
          </button>
          <button onClick={() => setActiveTab('email-templates')} className={`${activeTab === 'email-templates' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`} data-id="element-18713">
            <MailIcon className="h-5 w-5 mr-2" data-id="element-18714" />
            Email Templates
          </button>
        </nav>
      </div>
      {/* System Messages Tab Content */}
      {activeTab === 'messages' && <>
          {view === 'list' && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18715">
              <div className="px-4 py-5 sm:p-6" data-id="element-18716">
                <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 mb-6" data-id="element-18717">
                  <div className="relative flex-1 max-w-lg" data-id="element-18718">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-18719">
                      <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-18720" />
                    </div>
                    <input type="text" value={searchTerm} onChange={e => {
                setSearchTerm(e.target.value);
                setCurrentPage(1); // Reset to first page when searching
              }} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Search messages by key or content..." data-id="element-18721" />
                  </div>
                  <div className="flex space-x-2" data-id="element-18722">
                    <button onClick={() => setShowFilters(!showFilters)} className={`inline-flex items-center px-3 py-2 border ${showFilters ? 'border-indigo-500 text-indigo-700' : 'border-gray-300 text-gray-700'} rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-18723">
                      <FilterIcon className="h-4 w-4 mr-2" data-id="element-18724" />
                      Filters{' '}
                      {filters.type.length > 0 || filters.module.length > 0 || filters.hasOverride || filters.hasRbacRestriction ? `(${filters.type.length + filters.module.length + (filters.hasOverride ? 1 : 0) + (filters.hasRbacRestriction ? 1 : 0)})` : ''}
                    </button>
                    {(filters.type.length > 0 || filters.module.length > 0 || filters.hasOverride || !filters.isActive || filters.hasRbacRestriction || searchTerm) && <button onClick={resetFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18725">
                        <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-18726" />
                        Reset
                      </button>}
                  </div>
                </div>
                {showFilters && <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-18727">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-18728">
                      <div data-id="element-18729">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-18730">
                          Message Type
                        </h4>
                        <div className="space-y-2" data-id="element-18731">
                          {(['Error', 'Warning', 'Success', 'Info', 'Notification', 'System'] as MessageType[]).map(type => <div key={type} className="flex items-center" data-id="element-18732">
                              <input id={`filter-type-${type}`} name={`filter-type-${type}`} type="checkbox" checked={filters.type.includes(type)} onChange={() => toggleFilter('type', type)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18733" />
                              <label htmlFor={`filter-type-${type}`} className="ml-2 text-sm text-gray-700 flex items-center" data-id="element-18734">
                                {type === 'Error' && <XCircleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-18735" />}
                                {type === 'Warning' && <AlertTriangleIcon className="h-4 w-4 text-amber-500 mr-1" data-id="element-18736" />}
                                {type === 'Success' && <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-18737" />}
                                {type === 'Info' && <InfoIcon className="h-4 w-4 text-blue-500 mr-1" data-id="element-18738" />}
                                {type === 'Notification' && <BellIcon className="h-4 w-4 text-purple-500 mr-1" data-id="element-18739" />}
                                {type === 'System' && <SettingsIcon className="h-4 w-4 text-gray-500 mr-1" data-id="element-18740" />}
                                {type}
                              </label>
                            </div>)}
                        </div>
                      </div>
                      <div data-id="element-18741">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-18742">
                          Module
                        </h4>
                        <div className="space-y-2" data-id="element-18743">
                          {(['AP', 'PO', 'VM', 'OCR', 'DI', 'Financial', 'Settings', 'Core'] as MessageModule[]).map(module => <div key={module} className="flex items-center" data-id="element-18744">
                              <input id={`filter-module-${module}`} name={`filter-module-${module}`} type="checkbox" checked={filters.module.includes(module)} onChange={() => toggleFilter('module', module)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18745" />
                              <label htmlFor={`filter-module-${module}`} className="ml-2 text-sm text-gray-700" data-id="element-18746">
                                {module}
                              </label>
                            </div>)}
                        </div>
                      </div>
                      <div data-id="element-18747">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-18748">
                          Other Filters
                        </h4>
                        <div className="space-y-2" data-id="element-18749">
                          <div className="flex items-center" data-id="element-18750">
                            <input id="filter-has-override" name="filter-has-override" type="checkbox" checked={filters.hasOverride} onChange={() => {
                      setFilters({
                        ...filters,
                        hasOverride: !filters.hasOverride
                      });
                      setCurrentPage(1); // Reset to first page when filtering
                    }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18751" />
                            <label htmlFor="filter-has-override" className="ml-2 text-sm text-gray-700" data-id="element-18752">
                              Has Override
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-18753">
                            <input id="filter-is-active" name="filter-is-active" type="checkbox" checked={filters.isActive} onChange={() => {
                      setFilters({
                        ...filters,
                        isActive: !filters.isActive
                      });
                      setCurrentPage(1); // Reset to first page when filtering
                    }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18754" />
                            <label htmlFor="filter-is-active" className="ml-2 text-sm text-gray-700" data-id="element-18755">
                              Active Only
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-18756">
                            <input id="filter-has-rbac" name="filter-has-rbac" type="checkbox" checked={filters.hasRbacRestriction} onChange={() => {
                      setFilters({
                        ...filters,
                        hasRbacRestriction: !filters.hasRbacRestriction
                      });
                      setCurrentPage(1); // Reset to first page when filtering
                    }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18757" />
                            <label htmlFor="filter-has-rbac" className="ml-2 text-sm text-gray-700" data-id="element-18758">
                              Has RBAC Restriction
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
                {/* Pagination info */}
                <div className="flex justify-between items-center mb-4" data-id="element-18759">
                  <div className="text-sm text-gray-700" data-id="element-18760">
                    Showing{' '}
                    <span className="font-medium" data-id="element-18761">{startIndex + 1}</span> to{' '}
                    <span className="font-medium" data-id="element-18762">{endIndex}</span> of{' '}
                    <span className="font-medium" data-id="element-18763">{totalMessages}</span>{' '}
                    messages
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-18764">
                    <span className="text-sm text-gray-700" data-id="element-18765">
                      Rows per page:
                    </span>
                    <select value={pageSize} onChange={handlePageSizeChange} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18766">
                      <option value={5} data-id="element-18767">5</option>
                      <option value={10} data-id="element-18768">10</option>
                      <option value={25} data-id="element-18769">25</option>
                      <option value={50} data-id="element-18770">50</option>
                    </select>
                  </div>
                </div>
                <MessagesList messages={currentMessages} canEditMessage={canEditMessage} onEdit={handleEditMessage} onDelete={handleDeleteMessage} onResetOverride={handleResetOverride} locale={locale} data-id="element-18771" />
                {/* Pagination controls */}
                {totalMessages > 0 && <div className="mt-4 flex items-center justify-between" data-id="element-18772">
                    <div className="flex-1 flex justify-between sm:hidden" data-id="element-18773">
                      <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-18774">
                        Previous
                      </button>
                      <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-18775">
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-18776">
                      <div data-id="element-18777">
                        <p className="text-sm text-gray-700" data-id="element-18778">
                          Page{' '}
                          <span className="font-medium" data-id="element-18779">{currentPage}</span> of{' '}
                          <span className="font-medium" data-id="element-18780">{totalPages}</span>
                        </p>
                      </div>
                      <div data-id="element-18781">
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-18782">
                          <button onClick={() => goToPage(1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18783">
                            <span className="sr-only" data-id="element-18784">First Page</span>
                            <div className="h-5 w-5" aria-hidden="true" data-id="element-18785" />
                          </button>
                          <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18786">
                            <span className="sr-only" data-id="element-18787">Previous</span>
                            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-18788" />
                          </button>
                          {/* Page numbers */}
                          {Array.from({
                    length: Math.min(5, totalPages)
                  }, (_, i) => {
                    // Calculate page numbers to show
                    let pageNum;
                    if (totalPages <= 5) {
                      // If 5 or fewer pages, show all
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      // If near the start
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      // If near the end
                      pageNum = totalPages - 4 + i;
                    } else {
                      // If in the middle
                      pageNum = currentPage - 2 + i;
                    }
                    return <button key={pageNum} onClick={() => goToPage(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${currentPage === pageNum ? 'bg-indigo-50 border-indigo-500 text-indigo-600 z-10' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-18789">
                                  {pageNum}
                                </button>;
                  })}
                          <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18790">
                            <span className="sr-only" data-id="element-18791">Next</span>
                            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-18792" />
                          </button>
                          <button onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18793">
                            <span className="sr-only" data-id="element-18794">Last Page</span>
                            <div className="h-5 w-5" aria-hidden="true" data-id="element-18795" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>}
          {(view === 'add' || view === 'edit') && <MessageForm message={selectedMessage} availableRoles={availableRoles} currentUserRoles={currentUserRoles} onSave={handleSaveMessage} onCancel={() => setView('list')} locale={locale} data-id="element-18796" />}
        </>}
      {/* Email Templates Tab Content */}
      {activeTab === 'email-templates' && <>
          {emailView === 'list' && <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-18797">
              <div className="px-4 py-5 sm:p-6" data-id="element-18798">
                <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 mb-6" data-id="element-18799">
                  <div className="relative flex-1 max-w-lg" data-id="element-18800">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-18801">
                      <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-18802" />
                    </div>
                    <input type="text" value={emailSearchTerm} onChange={e => {
                setEmailSearchTerm(e.target.value);
                setEmailCurrentPage(1); // Reset to first page when searching
              }} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" placeholder="Search email templates by key or content..." data-id="element-18803" />
                  </div>
                  <div className="flex space-x-2" data-id="element-18804">
                    <button onClick={() => setShowEmailFilters(!showEmailFilters)} className={`inline-flex items-center px-3 py-2 border ${showEmailFilters ? 'border-indigo-500 text-indigo-700' : 'border-gray-300 text-gray-700'} rounded-md shadow-sm text-sm font-medium bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500`} data-id="element-18805">
                      <FilterIcon className="h-4 w-4 mr-2" data-id="element-18806" />
                      Filters{' '}
                      {emailFilters.module.length > 0 || emailFilters.hasRbacRestriction ? `(${emailFilters.module.length + (emailFilters.hasRbacRestriction ? 1 : 0)})` : ''}
                    </button>
                    {(emailFilters.module.length > 0 || !emailFilters.isActive || emailFilters.hasRbacRestriction || emailSearchTerm) && <button onClick={resetEmailFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-18807">
                        <RefreshCwIcon className="h-4 w-4 mr-2" data-id="element-18808" />
                        Reset
                      </button>}
                  </div>
                </div>
                {showEmailFilters && <div className="bg-gray-50 p-4 rounded-md mb-6" data-id="element-18809">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-18810">
                      <div data-id="element-18811">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-18812">
                          Module
                        </h4>
                        <div className="space-y-2" data-id="element-18813">
                          {(['AP', 'PO', 'VM', 'OCR', 'DI', 'Financial', 'Settings', 'Core'] as MessageModule[]).map(module => <div key={module} className="flex items-center" data-id="element-18814">
                              <input id={`email-filter-module-${module}`} name={`email-filter-module-${module}`} type="checkbox" checked={emailFilters.module.includes(module)} onChange={() => toggleEmailFilter('module', module)} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18815" />
                              <label htmlFor={`email-filter-module-${module}`} className="ml-2 text-sm text-gray-700" data-id="element-18816">
                                {module}
                              </label>
                            </div>)}
                        </div>
                      </div>
                      <div data-id="element-18817">
                        <h4 className="text-sm font-medium text-gray-700 mb-2" data-id="element-18818">
                          Other Filters
                        </h4>
                        <div className="space-y-2" data-id="element-18819">
                          <div className="flex items-center" data-id="element-18820">
                            <input id="email-filter-is-active" name="email-filter-is-active" type="checkbox" checked={emailFilters.isActive} onChange={() => {
                      setEmailFilters({
                        ...emailFilters,
                        isActive: !emailFilters.isActive
                      });
                      setEmailCurrentPage(1); // Reset to first page when filtering
                    }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18821" />
                            <label htmlFor="email-filter-is-active" className="ml-2 text-sm text-gray-700" data-id="element-18822">
                              Active Only
                            </label>
                          </div>
                          <div className="flex items-center" data-id="element-18823">
                            <input id="email-filter-has-rbac" name="email-filter-has-rbac" type="checkbox" checked={emailFilters.hasRbacRestriction} onChange={() => {
                      setEmailFilters({
                        ...emailFilters,
                        hasRbacRestriction: !emailFilters.hasRbacRestriction
                      });
                      setEmailCurrentPage(1); // Reset to first page when filtering
                    }} className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded" data-id="element-18824" />
                            <label htmlFor="email-filter-has-rbac" className="ml-2 text-sm text-gray-700" data-id="element-18825">
                              Has RBAC Restriction
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}
                {/* Pagination info for email templates */}
                <div className="flex justify-between items-center mb-4" data-id="element-18826">
                  <div className="text-sm text-gray-700" data-id="element-18827">
                    Showing{' '}
                    <span className="font-medium" data-id="element-18828">{emailStartIndex + 1}</span>{' '}
                    to <span className="font-medium" data-id="element-18829">{emailEndIndex}</span> of{' '}
                    <span className="font-medium" data-id="element-18830">{totalEmailTemplates}</span>{' '}
                    email templates
                  </div>
                  <div className="flex items-center space-x-2" data-id="element-18831">
                    <span className="text-sm text-gray-700" data-id="element-18832">
                      Rows per page:
                    </span>
                    <select value={emailPageSize} onChange={handleEmailPageSizeChange} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" data-id="element-18833">
                      <option value={5} data-id="element-18834">5</option>
                      <option value={10} data-id="element-18835">10</option>
                      <option value={25} data-id="element-18836">25</option>
                      <option value={50} data-id="element-18837">50</option>
                    </select>
                  </div>
                </div>
                <EmailTemplatesList emailTemplates={currentEmailTemplates} canEditEmailTemplate={canEditEmailTemplate} onEdit={handleEditEmailTemplate} onDelete={handleDeleteEmailTemplate} onSendTest={handleSendTestEmail} locale={locale} data-id="element-18838" />
                {/* Pagination controls for email templates */}
                {totalEmailTemplates > 0 && <div className="mt-4 flex items-center justify-between" data-id="element-18839">
                    <div className="flex-1 flex justify-between sm:hidden" data-id="element-18840">
                      <button onClick={() => goToEmailPage(emailCurrentPage - 1)} disabled={emailCurrentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${emailCurrentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-18841">
                        Previous
                      </button>
                      <button onClick={() => goToEmailPage(emailCurrentPage + 1)} disabled={emailCurrentPage === emailTotalPages} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${emailCurrentPage === emailTotalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-18842">
                        Next
                      </button>
                    </div>
                    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-18843">
                      <div data-id="element-18844">
                        <p className="text-sm text-gray-700" data-id="element-18845">
                          Page{' '}
                          <span className="font-medium" data-id="element-18846">
                            {emailCurrentPage}
                          </span>{' '}
                          of{' '}
                          <span className="font-medium" data-id="element-18847">{emailTotalPages}</span>
                        </p>
                      </div>
                      <div data-id="element-18848">
                        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-18849">
                          <button onClick={() => goToEmailPage(1)} disabled={emailCurrentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${emailCurrentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18850">
                            <span className="sr-only" data-id="element-18851">First Page</span>
                            <div className="h-5 w-5" aria-hidden="true" data-id="element-18852" />
                          </button>
                          <button onClick={() => goToEmailPage(emailCurrentPage - 1)} disabled={emailCurrentPage === 1} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${emailCurrentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18853">
                            <span className="sr-only" data-id="element-18854">Previous</span>
                            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-18855" />
                          </button>
                          {/* Page numbers */}
                          {Array.from({
                    length: Math.min(5, emailTotalPages)
                  }, (_, i) => {
                    // Calculate page numbers to show
                    let pageNum;
                    if (emailTotalPages <= 5) {
                      // If 5 or fewer pages, show all
                      pageNum = i + 1;
                    } else if (emailCurrentPage <= 3) {
                      // If near the start
                      pageNum = i + 1;
                    } else if (emailCurrentPage >= emailTotalPages - 2) {
                      // If near the end
                      pageNum = emailTotalPages - 4 + i;
                    } else {
                      // If in the middle
                      pageNum = emailCurrentPage - 2 + i;
                    }
                    return <button key={pageNum} onClick={() => goToEmailPage(pageNum)} className={`relative inline-flex items-center px-4 py-2 border ${emailCurrentPage === pageNum ? 'bg-indigo-50 border-indigo-500 text-indigo-600 z-10' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'} text-sm font-medium`} data-id="element-18856">
                                  {pageNum}
                                </button>;
                  })}
                          <button onClick={() => goToEmailPage(emailCurrentPage + 1)} disabled={emailCurrentPage === emailTotalPages} className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${emailCurrentPage === emailTotalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18857">
                            <span className="sr-only" data-id="element-18858">Next</span>
                            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-18859" />
                          </button>
                          <button onClick={() => goToEmailPage(emailTotalPages)} disabled={emailCurrentPage === emailTotalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${emailCurrentPage === emailTotalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-18860">
                            <span className="sr-only" data-id="element-18861">Last Page</span>
                            <div className="h-5 w-5" aria-hidden="true" data-id="element-18862" />
                          </button>
                        </nav>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>}
          {(emailView === 'add' || emailView === 'edit') && <EmailTemplateForm template={selectedTemplate} availableRoles={availableRoles} currentUserRoles={currentUserRoles} onSave={handleSaveEmailTemplate} onCancel={() => setEmailView('list')} onSendTest={handleSendTestEmail} locale={locale} data-id="element-18863" />}
        </>}
    </div>;
}